<script>
import RecipeMealStatus from '@components/notifications/recipe-meal-status.vue';
import { breakpointsMixin } from '@mixins/breakpoints-mixin';
import {
  authComputed,
  platelistComputed,
  platelistMethods,
} from '@state/helpers';

export default {
  components: {
    RecipeMealStatus,
  },
  mixins: [breakpointsMixin],
  props: {
    planWeek: {
      type: Object,
      required: true,
      default: () => {},
    },
  },
  data() {
    return {
      isGroceryEnabled: false,
      isCopyWeekEnabled: false,
    };
  },
  computed: {
    ...authComputed,
    ...platelistComputed,
    weekTitle() {
      return this.planWeek && this.planWeek.weekOf;
    },
    isPlatelistLocked() {
      return (
        this.selectedPlatelist &&
        this.selectedPlatelist.editabilityStatus &&
        this.selectedPlatelist.editabilityStatus.locked
      );
    },
    isDraft() {
      return this.selectedPlatelist && this.selectedPlatelist.draft;
    },
    isPublished() {
      return (
        this.selectedPlatelist &&
        !this.selectedPlatelist.archived &&
        !this.selectedPlatelist.draft
      );
    },
    isArchived() {
      return (
        this.selectedPlatelist &&
        this.selectedPlatelist.archived &&
        !this.selectedPlatelist.draft
      );
    },
    isFlagged() {
      return this.selectedPlatelist && this.selectedPlatelist.flagged;
    },
    isPublishedMealPlanFlagged() {
      return (
        this.selectedPlatelist &&
        !this.selectedPlatelist.draft &&
        this.selectedPlatelist.flagged
      );
    },
    isDraftMealPlanFlagged() {
      return (
        this.selectedPlatelist &&
        this.selectedPlatelist.draft &&
        this.selectedPlatelist.flagged
      );
    },
    mealPlanStatus() {
      const status = this.selectedPlatelist.draft
        ? `Draft`
        : !this.selectedPlatelist.draft && this.selectedPlatelist.flagged
        ? `Flagged`
        : '';
      return status;
    },
    mealPlanStatusText() {
      if (this.selectedPlatelist) {
        const statusText =
          this.selectedPlatelist.draft && this.selectedPlatelist.flagged
            ? `This draft has been flagged for review.`
            : this.selectedPlatelist.draft && !this.selectedPlatelist.flagged
            ? `This meal plan has not been published.`
            : !this.selectedPlatelist.draft && this.selectedPlatelist.flagged
            ? `This meal plan has been flagged for review.`
            : '';
        return statusText;
      } else {
        return '';
      }
    },
  },
  methods: {
    ...platelistMethods,
    showPlanForm() {
      this.$emit('show-plan-form');
    },
    showGroceryList() {
      this.$emit('show-grocery-panel');
    },
    getPlanGrid() {
      this.getAdminPlanGrid({
        platelistId: this.selectedPlatelist.id,
        weekOffset: this.selectedWeekOffset,
      });
    },
    async nextWeek() {
      await this.setSelectedWeekOffset(this.selectedWeekOffset + 1);
      this.getPlanGrid();
    },
    async prevWeek() {
      if (this.selectedWeekOffset > 0) {
        await this.setSelectedWeekOffset(this.selectedWeekOffset - 1);
        this.getPlanGrid();
      }
    },
  },
};
</script>

<template>
  <div>
    <RecipeMealStatus
      v-if="!$_isXLTabletUp() && (isDraft || isFlagged)"
      style="padding-top: 1rem;"
      :isDraft="isDraft"
      :isFlagged="isFlagged"
      :isPublishedFlagged="isPublishedMealPlanFlagged"
      :isDraftFlagged="isDraftMealPlanFlagged"
      :status="mealPlanStatus"
      :statusText="mealPlanStatusText"
    />
    <div class="plan-header">
      <div class="header-title-wrapper" v-if="selectedPlatelist">
        <h6 class="section-title">PLAN TITLE</h6>
        <h2>
          {{ selectedPlatelist.title }}
          <BaseButton
            class="btn-edit"
            v-if="!isPlatelistLocked"
            @click="showPlanForm"
          >
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg" v-bind:class="'icon-edit'" v-bind:svg-inline="''" v-bind:role="'presentation'" v-bind:focusable="'false'" v-bind:tabindex="'-1'"><path d="M7 11.666h5.25M9.875 2.362a1.237 1.237 0 011.75 1.75l-7.292 7.292L2 11.987l.583-2.333 7.292-7.292z" stroke="#000" stroke-linecap="round" stroke-linejoin="round"/></svg>
            Edit
          </BaseButton>
        </h2>
      </div>
      <RecipeMealStatus
        v-if="$_isXLTabletUp() && (isDraft || isFlagged)"
        :isDraft="isDraft"
        :isFlagged="isFlagged"
        :isPublishedFlagged="isPublishedMealPlanFlagged"
        :isDraftFlagged="isDraftMealPlanFlagged"
        :status="mealPlanStatus"
        :statusText="mealPlanStatusText"
      />
      <div class="plan-header-actions">
        <div class="section-week">
          <span class="icon-caret left" @click="prevWeek">
            <img src="@assets/images/icon-previous.svg" />
          </span>
          <h4>{{ weekTitle }}</h4>
          <span class="icon-caret right" @click="nextWeek">
            <img src="@assets/images/icon-next.svg" />
          </span>
          <BaseButton class="btn-copy" v-if="isCopyWeekEnabled">
            <img src="@assets/images/icon-copy-week.svg" />
          </BaseButton>
        </div>
        <BaseButton
          class="btn-secondary btn-grocery-list"
          @click="showGroceryList"
          v-if="isGroceryEnabled"
        >
          Show Grocery List
        </BaseButton>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import '@design';

.plan-header {
  align-items: center;
  display: flex;
  justify-content: space-between;
  padding: 1.4rem 3.4rem 1.4rem 0.8rem;
}

.header-title-wrapper {
  flex: 1;

  h2 {
    align-items: center;
    display: flex;

    .btn-edit {
      align-items: center;
      background: none;
      border: 2px solid $color-action;
      border-radius: 1.4rem;
      color: black;
      display: flex;
      margin-left: 2rem;
      padding: 0.4rem 1.5rem;

      .icon-edit {
        margin-right: 0.5rem;
      }

      &:hover {
        background: $color-action;
        color: white;

        .icon-edit {
          path {
            stroke: white;
          }
        }
      }
    }
  }
}

.plan-header-actions {
  display: flex;
  flex: 1;
  justify-content: flex-end;

  .section-week {
    align-content: center;
    display: flex;

    .icon-caret {
      &:hover {
        cursor: pointer;
      }

      &.left {
        margin-right: 2rem;
      }

      &.right {
        margin-left: 2rem;
      }
    }

    h4 {
      font-size: 1.4rem;
      margin-bottom: 0;
    }

    .btn-copy {
      background: none;
      color: black;
      padding: 0 1rem;
    }
  }

  .btn-secondary.btn-grocery-list {
    border-color: $color-yellow;
    border-radius: 2rem;
    color: black;

    &:hover {
      background: $color-yellow;
      color: black;
    }
  }
}
</style>
